<template>
    <!--
      StockCategoriesSection
      Gradient section displaying categorized stock cards with carousel navigation.
      Each category row shows up to `visibleCount` cards; categories with more items
      get left/right navigation arrows (as per Figma design node 943-1674).
    -->
    <section class="py-16 lg:py-[100px] overflow-hidden" :style="{ background: backgroundColor }">
        <div class="">
            <!-- ── Header ── -->
            <div class="flex lg:flex-row flex-col-reverse items-start lg:gap-0 gap-[30px] justify-between mb-[50px] px-5 lg:px-[120px]">
                <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold" :class="titleColor">
                    <span v-html="renderedTitle"></span>
                </h2>
                <!-- Optional logo icon -->
                <img
                    v-if="iconImage"
                    :src="$asset(iconImage, 'public')"
                    class="w-[70px] h-[70px] lg:w-[114px] lg:h-[114px] object-contain rounded-xl block"
                    :style="{ backgroundColor: iconBg }"
                    alt="section icon"
                />
            </div>

            <!-- ── Subtitle ── -->
            <p v-if="subtitle" class="text-[16px] lg:text-[18px] font-pro leading-relaxed mb-[50px] px-5 lg:px-[120px]" :class="subtitleColor">
                {{ subtitle }}
            </p>

            <!-- ── Categories ── -->
            <div class="flex flex-col gap-[50px]">
                <div v-for="(category, catIdx) in categories" :key="catIdx">
                    <!-- Category header -->
                    <div :class="[gridMode ? 'justify-center' : 'justify-between']" class="flex flex-row items-center mb-[20px] mx-[20px] lg:mx-[120px]">
                        <h3 class="text-[20px] lg:text-[24px] font-bold" :style="{ color: categoryTitleColor }">
                            {{ category.title }}
                        </h3>

                        <!-- Navigation arrows (desktop only, only when needed, hidden in gridMode) -->
                        <div v-if="!gridMode && category.stocks.length > desktopVisible" class="hidden lg:flex gap-[12px]">
                            <button
                                class="w-8 h-8 rounded-full flex items-center justify-center shadow-sm transition"
                                :class="offsets[catIdx] === 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'"
                                :disabled="offsets[catIdx] === 0"
                                aria-label="Zurück"
                                @click="prev(catIdx)"
                            >
                                <i class="fi fi-rs-angle-left text-white"></i>
                            </button>
                            <button
                                class="w-8 h-8 rounded-full flex items-center justify-center shadow-sm transition"
                                :class="offsets[catIdx] >= maxOffset(category) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'"
                                :disabled="offsets[catIdx] >= maxOffset(category)"
                                aria-label="Weiter"
                                @click="next(catIdx, category)"
                            >
                                <i class="fi fi-rs-angle-right"></i>
                            </button>
                        </div>
                    </div>

                    <!-- ── Cards container (Grid Mode) ── -->
                    <div v-if="gridMode" class="px-5 lg:px-[120px]">
                        <!-- Desktop: centered grid with 4 columns -->
                        <div class="hidden lg:grid lg:grid-cols-4 gap-5 justify-items-center">
                            <div
                                v-for="(stock, stockIdx) in category.stocks"
                                :key="stockIdx"
                                class="flex flex-col items-center justify-center gap-[14px] rounded-xl py-[20px] px-[20px] w-full"
                                :style="{ backgroundColor: cardBg, minHeight: '156px' }"
                            >
                                <div class="w-[80px] h-[80px] rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" :style="{ backgroundColor: logoSlotBg }">
                                    <img v-if="stock.image" :src="$asset(stock.image, 'public')" :alt="stock.name" class="w-full h-full object-contain p-[8px]" />
                                    <span v-else class="text-[22px] font-bold select-none" :style="{ color: logoInitialColor }">
                                        {{ stock.name.charAt(0) }}
                                    </span>
                                </div>
                                <p class="text-center font-bold text-[16px] leading-[22px] w-full" :style="{ color: stockNameColor }">
                                    {{ stock.name }}
                                </p>
                            </div>
                        </div>
                        <!-- Mobile: 2-column grid -->
                        <div class="lg:hidden grid grid-cols-2 gap-4">
                            <div
                                v-for="(stock, stockIdx) in category.stocks"
                                :key="stockIdx"
                                class="flex flex-col items-center justify-center gap-[10px] rounded-xl py-[16px] px-[16px]"
                                :style="{ backgroundColor: cardBg, minHeight: '130px' }"
                            >
                                <div class="w-[80px] h-[80px] rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" :style="{ backgroundColor: logoSlotBg }">
                                    <img v-if="stock.image" :src="$asset(stock.image, 'public')" :alt="stock.name" class="w-full h-full object-contain p-[6px]" />
                                    <span v-else class="text-[18px] font-bold select-none" :style="{ color: logoInitialColor }">
                                        {{ stock.name.charAt(0) }}
                                    </span>
                                </div>
                                <p class="text-center font-bold text-[13px] leading-[18px] w-full" :style="{ color: stockNameColor }">
                                    {{ stock.name }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- ── Cards container (Carousel Mode) ── -->
                    <template v-else>
                        <!-- Desktop: carousel slide -->
                        <div class="hidden lg:block overflow-hidden">
                            <div
                                class="flex gap-5 transition-transform duration-300 ease-in-out"
                                :style="{ transform: `translateX(calc(${cardGap}px - ${offsets[catIdx] * (cardWidth + cardGap)}px))` }"
                            >
                                <div
                                    v-for="(stock, stockIdx) in category.stocks"
                                    :key="stockIdx"
                                    class="flex flex-col items-center justify-center gap-[14px] rounded-xl flex-shrink-0 py-[20px] px-[20px]"
                                    :style="{
                                        backgroundColor: cardBg,
                                        width: cardWidth + 'px',
                                        minHeight: '156px'
                                    }"
                                >
                                    <div
                                        class="w-[80px] h-[80px] rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
                                        :style="{ backgroundColor: logoSlotBg }"
                                    >
                                        <img v-if="stock.image" :src="$asset(stock.image, 'public')" :alt="stock.name" class="w-full h-full object-contain p-[8px]" />
                                        <span v-else class="text-[22px] font-bold select-none" :style="{ color: logoInitialColor }">
                                            {{ stock.name.charAt(0) }}
                                        </span>
                                    </div>
                                    <p class="text-center font-bold text-[16px] leading-[22px] w-full" :style="{ color: stockNameColor }">
                                        {{ stock.name }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Mobile: horizontal scroll -->
                        <div class="lg:hidden flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                            <div
                                v-for="(stock, stockIdx) in category.stocks"
                                :key="stockIdx"
                                class="flex flex-col items-center justify-center gap-[10px] rounded-xl flex-shrink-0 py-[16px] px-[16px]"
                                :style="{
                                    backgroundColor: cardBg,
                                    width: '160px',
                                    minHeight: '130px'
                                }"
                            >
                                <div class="w-[80px] h-[80px] rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" :style="{ backgroundColor: logoSlotBg }">
                                    <img v-if="stock.image" :src="$asset(stock.image, 'public')" :alt="stock.name" class="w-full h-full object-contain p-[6px]" />
                                    <span v-else class="text-[18px] font-bold select-none" :style="{ color: logoInitialColor }">
                                        {{ stock.name.charAt(0) }}
                                    </span>
                                </div>
                                <p class="text-center font-bold text-[13px] leading-[18px] w-full" :style="{ color: stockNameColor }">
                                    {{ stock.name }}
                                </p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'linear-gradient(to bottom right, #11D2D4, #0C7475)'
    },
    title: {
        type: String,
        default: ''
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: 'text-black'
    },
    subtitle: {
        type: String,
        default: ''
    },
    subtitleColor: {
        type: String,
        default: 'text-white'
    },
    iconImage: {
        type: String,
        default: ''
    },
    iconBg: {
        type: String,
        default: '#FFFFFF'
    },
    categories: {
        type: Array,
        default: () => []
    },
    cardBg: {
        type: String,
        default: '#ffffff'
    },
    categoryTitleColor: {
        type: String,
        default: '#000000'
    },
    stockNameColor: {
        type: String,
        default: '#000000'
    },
    logoSlotBg: {
        type: String,
        default: '#f0fafa'
    },
    logoInitialColor: {
        type: String,
        default: '#0C7475'
    },
    /** Number of cards visible at once on desktop */
    desktopVisible: {
        type: Number,
        default: 5
    },
    /** Grid mode: show all cards in a centered 4-column grid without arrows */
    gridMode: {
        type: Boolean,
        default: false
    }
});

/** Card width & gap (px) – matches Figma: 224px card, 20px gap */
const cardWidth = 224;
const cardGap = 115;

/** Per-category carousel offset (number of cards scrolled) */
const offsets = ref(props.categories.map(() => 0));

const maxOffset = (category) => Math.max(0, category.stocks.length - props.desktopVisible);

function prev(catIdx) {
    if (offsets.value[catIdx] > 0) {
        offsets.value[catIdx]--;
    }
}

function next(catIdx, category) {
    if (offsets.value[catIdx] < maxOffset(category)) {
        offsets.value[catIdx]++;
    }
}

/** Render title with optional highlighted portion */
const renderedTitle = computed(() => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length < 2) return props.title;

    return parts
        .map((part, index) => {
            if (index === 0) return part;

            return `<span class="${props.titleHighlightColor}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
});
</script>

<style scoped>
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
